import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Clock, Package as PackageIcon } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { useCurrency } from '@/Hooks/useCurrency';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { LoadingState } from '@/components/ui/states';
import { Table, TableEmpty, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { useVisitPicker } from '@/Pages/Spaces/lib/visits';
import { VisitPicker } from '@/Pages/Spaces/components/VisitPicker';
import { fromMinor, idempotencyKey, toMinor } from '@/Pages/Billing/lib';
import type { EntitlementStatus } from '@/lib/shared-types';
import { apiError, type EntitlementType, type Package } from './lib';

/** An issued grant with the balance the API projects onto it (§6.3). */
export interface IssuedEntitlement {
  id: string;
  packageId: string;
  checkId: string | null;
  type: EntitlementType;
  status: EntitlementStatus;
  quantity: string;
  consumedQuantity: string;
  remainingQuantity: string;
  minutes: number | null;
  consumedMinutes: number;
  remainingMinutes: number | null;
  isExpired: boolean;
  issuedAt: string | null;
  expiresAt: string | null;
  package?: { id: string; name: string } | null;
  catalogItem?: { id: string; name: string } | null;
  resource?: { id: string; name: string; type: string } | null;
  check?: { id: string; number: string; status: string } | null;
}

/** What `POST /packages/entitlements/:id/consume` answers with. */
interface ConsumptionResult {
  entitlement: IssuedEntitlement;
  consumedQuantity: string;
  consumedMinutes: number;
}

/** `1.000` reads as noise on a till; `1` and `1.5` are the same number. */
function trimQuantity(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/, '') || '0' : value;
}

/** How much of the grant is still there, 0…1, for the meter. */
function fractionLeft(row: IssuedEntitlement): number {
  if (row.type === 'RESOURCE_TIME') {
    const granted = row.minutes ?? 0;
    return granted > 0 ? Math.min(1, Math.max(0, (row.remainingMinutes ?? 0) / granted)) : 0;
  }
  const granted = Number(row.quantity);
  return granted > 0 ? Math.min(1, Math.max(0, Number(row.remainingQuantity) / granted)) : 0;
}

/** A grant that can still be drawn against right now. */
function isRedeemable(row: IssuedEntitlement): boolean {
  if (row.isExpired) return false;
  if (row.status !== 'ISSUED' && row.status !== 'PARTIALLY_CONSUMED') return false;
  return row.type === 'RESOURCE_TIME'
    ? (row.remainingMinutes ?? 0) > 0
    : Number(row.remainingQuantity) > 0;
}

/** Re-renders on an interval so "expires in 12m" is not frozen at page load. */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * C-09 — the issued side of §6.3. The catalog defines packages; this is what a
 * guest actually holds: who it was sold to, how much is left on it, when it
 * expires, plus the two things that move a balance — selling a package to a
 * guest and redeeming against what they hold — and the void path when a sale is
 * reversed.
 */
export function IssuedEntitlements({ pkg }: { pkg: Package | null }) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { confirm } = useConfirm();
  // Mirrors the controller's own decorators (I-06): selling mints stored value
  // (`packages.sell`), a draw spends it (`entitlements.consume`) and a write-off
  // destroys it (`entitlements.cancel`). None of the three is an order edit.
  const canSell = can('packages.sell');
  const canConsume = can('entitlements.consume');
  const canCancel = can('entitlements.cancel');

  const [rows, setRows] = useState<IssuedEntitlement[]>([]);
  const [loading, setLoading] = useState(false);
  const [redeemableOnly, setRedeemableOnly] = useState(true);
  const [sellOpen, setSellOpen] = useState(false);
  const [consuming, setConsuming] = useState<IssuedEntitlement | null>(null);
  const now = useNow(30_000);

  const load = useCallback(() => {
    if (!pkg) {
      setRows([]);
      return;
    }
    setLoading(true);
    api
      .get<{ data: IssuedEntitlement[] }>('/packages/entitlements', {
        params: {
          packageId: pkg.id,
          limit: 100,
          sortBy: 'issuedAt',
          sortOrder: 'desc',
          ...(redeemableOnly ? { redeemableOnly: true } : {}),
        },
      })
      .then(({ data }) => setRows(data.data ?? []))
      .catch((e) => toast.error(apiError(e, t('catalog.entitlements.loadFailed'))))
      .finally(() => setLoading(false));
  }, [pkg, redeemableOnly, t]);

  useEffect(load, [load]);

  const cancel = async (row: IssuedEntitlement) => {
    if (!(await confirm({ title: t('catalog.entitlements.cancelConfirm'), tone: 'danger' }))) return;
    try {
      await api.post(`/packages/entitlements/${row.id}/cancel`, {});
      toast.success(t('catalog.entitlements.cancelled'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('catalog.entitlements.cancelFailed')));
    }
  };

  const formatDate = (value: string) => new Date(value).toLocaleString(i18n.language);

  /** Expiry as a state, not a raw timestamp: none / expired / counting down. */
  const expiry = (row: IssuedEntitlement) => {
    if (!row.expiresAt) {
      return { tone: 'neutral' as const, label: t('catalog.packages.visitOnly'), title: '' };
    }
    const at = new Date(row.expiresAt);
    const minutesLeft = Math.floor((at.getTime() - now) / 60_000);
    if (row.isExpired || minutesLeft < 0) {
      return {
        tone: 'danger' as const,
        label: t('catalog.entitlements.expiredAt', { at: formatDate(row.expiresAt) }),
        title: formatDate(row.expiresAt),
      };
    }
    const hours = Math.floor(minutesLeft / 60);
    const label =
      hours > 0
        ? t('catalog.entitlements.expiresInHours', { hours, minutes: minutesLeft % 60 })
        : t('catalog.entitlements.expiresInMinutes', { minutes: minutesLeft });
    // Under an hour left is the state a cashier has to act on, so it is coloured.
    return {
      tone: minutesLeft < 60 ? ('warning' as const) : ('neutral' as const),
      label,
      title: formatDate(row.expiresAt),
    };
  };

  if (!pkg) return null;

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
        <div>
          <CardTitle>{t('catalog.entitlements.title')}</CardTitle>
          <p className="text-xs text-ink-muted">
            {t('catalog.entitlements.subtitle', { name: pkg.name })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={redeemableOnly}
              onChange={(event) => setRedeemableOnly(event.currentTarget.checked)}
            />
            {t('catalog.entitlements.redeemableOnly')}
          </label>
          {canSell && (
            <Button size="sm" onClick={() => setSellOpen(true)}>
              {t('catalog.entitlements.sell')}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <LoadingState label={t('common.loading')} />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>{t('catalog.entitlements.billedTo')}</TH>
                  <TH>{t('catalog.entitlements.grant')}</TH>
                  <TH>{t('catalog.entitlements.remaining')}</TH>
                  <TH>{t('catalog.entitlements.expires')}</TH>
                  <TH>{t('common.status')}</TH>
                  <TH className="text-end">{t('common.actions')}</TH>
                </tr>
              </THead>
              <tbody>
                {rows.length === 0 ? (
                  <TableEmpty colSpan={6}>{t('catalog.entitlements.empty')}</TableEmpty>
                ) : (
                  rows.map((row) => {
                    const state = expiry(row);
                    return (
                      <TR key={row.id}>
                        <TD>
                          {/* The bill that bought the grant. It used to be the
                              guest who held it, and a `PartyParticipant` died
                              with its party — a check is what survives a visit
                              and what a till can look up. */}
                          <span className="font-medium">
                            {row.check
                              ? t('catalog.entitlements.checkNumber', { number: row.check.number })
                              : t('catalog.entitlements.noCheck')}
                          </span>
                        </TD>
                        <TD>
                          <span className="flex items-center gap-2">
                            {row.type === 'RESOURCE_TIME' ? (
                              <Clock className="h-4 w-4 text-accent" aria-hidden />
                            ) : (
                              <PackageIcon className="h-4 w-4 text-accent" aria-hidden />
                            )}
                            {row.type === 'RESOURCE_TIME'
                              ? t('catalog.packages.minutesOn', {
                                  minutes: row.minutes ?? 0,
                                  target: row.resource?.name ?? t('catalog.packages.anyResource'),
                                })
                              : t('catalog.packages.itemGrant', {
                                  quantity: trimQuantity(row.quantity),
                                  name: row.catalogItem?.name ?? '',
                                })}
                          </span>
                        </TD>
                        <TD>
                          <BalanceCell row={row} />
                        </TD>
                        <TD className="text-xs" title={state.title}>
                          <span
                            className={
                              state.tone === 'danger'
                                ? 'text-red-600 dark:text-red-400'
                                : state.tone === 'warning'
                                  ? 'text-amber-600'
                                  : 'text-ink-muted'
                            }
                          >
                            {state.label}
                          </span>
                        </TD>
                        <TD>
                          <Badge tone={row.isExpired ? 'danger' : toneForStatus(row.status)}>
                            {t(`catalog.entitlements.status.${row.status}`, row.status)}
                          </Badge>
                        </TD>
                        <TD className="text-end">
                          <span className="flex justify-end gap-1">
                            {canConsume && isRedeemable(row) && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setConsuming(row)}
                              >
                                {t('catalog.entitlements.redeem')}
                              </Button>
                            )}
                            {canCancel &&
                              (row.status === 'ISSUED' || row.status === 'PARTIALLY_CONSUMED') && (
                                <Button size="sm" variant="ghost" onClick={() => void cancel(row)}>
                                  {t('catalog.entitlements.cancel')}
                                </Button>
                              )}
                          </span>
                        </TD>
                      </TR>
                    );
                  })
                )}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </CardContent>

      {/* Both dialogs are mounted only while open, so each sale and each draw
          gets a fresh idempotency key — and a retry after a failed request
          reuses the same one, which is the whole point of the key. */}
      {sellOpen && <SellPackageDialog pkg={pkg} onClose={() => setSellOpen(false)} onSold={load} />}
      {consuming && (
        <ConsumeDialog row={consuming} onClose={() => setConsuming(null)} onConsumed={load} />
      )}
    </Card>
  );
}

/** Remaining balance as a number *and* a bar — "how much is left" at a glance. */
function BalanceCell({ row }: { row: IssuedEntitlement }) {
  const { t } = useTranslation();
  const left = fractionLeft(row);
  const empty = left <= 0;

  return (
    <div className="min-w-32">
      <span className={`tabular-nums text-sm ${empty ? 'text-ink-muted' : ''}`}>
        {row.type === 'RESOURCE_TIME'
          ? t('catalog.entitlements.minutesOfMinutes', {
              remaining: row.remainingMinutes ?? 0,
              granted: row.minutes ?? 0,
            })
          : t('catalog.entitlements.unitsLeft', {
              remaining: trimQuantity(row.remainingQuantity),
              granted: trimQuantity(row.quantity),
            })}
      </span>
      <div
        className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-line"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(left * 100)}
      >
        <div
          className={`h-full rounded-full ${
            empty ? 'bg-line-strong' : row.isExpired ? 'bg-red-400' : 'bg-accent-solid'
          }`}
          style={{ width: `${Math.round(left * 100)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * §6.3 — issue a package against a bill. One check, one or more copies.
 *
 * The grants used to be issued to a `PartyParticipant` and `checkId` was an
 * optional extra; the check is the only holder now. That is not a smaller
 * question wearing the same clothes — it is the one the resource session asks
 * back when the guest comes to spend the minutes, because a grant is looked up
 * by the check that bought it.
 */
function SellPackageDialog({
  pkg,
  onClose,
  onSold,
}: {
  pkg: Package;
  onClose: () => void;
  onSold: () => void;
}) {
  const { t } = useTranslation();
  const { code, format } = useCurrency();
  const picker = useVisitPicker(true);
  const [quantity, setQuantity] = useState('1');
  const [busy, setBusy] = useState(false);
  // §34 — one key for this sale, kept across retries: a request that timed out
  // after the server issued the grants replays into the same grants, not a
  // second package.
  const [key] = useState(() => idempotencyKey(`sell:${pkg.id}`));

  const copies = Math.max(1, Math.round(Number(quantity) || 1));
  const grants = useMemo(() => pkg.entitlements ?? [], [pkg.entitlements]);
  // Minor units for the multiply — `Number('0.1') * 3` is not 0.3.
  const total = useMemo(
    () => fromMinor(toMinor(pkg.price, code) * copies, code),
    [pkg.price, code, copies],
  );

  const submit = async () => {
    if (!picker.checkId) return;
    setBusy(true);
    try {
      await api.post(`/packages/${pkg.id}/sell`, {
        checkId: picker.checkId,
        quantity: copies,
        idempotencyKey: key,
      });
      toast.success(t('catalog.entitlements.sold'));
      onSold();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('catalog.entitlements.sellFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('catalog.entitlements.sellTitle', { name: pkg.name })}
      description={t('catalog.entitlements.sellHint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !picker.checkId}>
            {t('catalog.entitlements.sell')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <VisitPicker
          visits={picker.visits}
          sessionId={picker.sessionId}
          onChange={picker.setSessionId}
          check={picker.check}
          loading={picker.loading}
          hint={t('catalog.entitlements.visitHint')}
        />

        <Field label={t('common.quantity')} hint={t('catalog.entitlements.copiesHint')}>
          <Input
            type="number"
            min={1}
            max={50}
            value={quantity}
            onChange={(event) => setQuantity(event.currentTarget.value)}
          />
        </Field>

        <div className="rounded-lg border p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium">{t('catalog.entitlements.willIssue')}</p>
            <p className="text-sm font-semibold tabular-nums">{format(total)}</p>
          </div>
          {grants.length === 0 ? (
            <p className="mt-1 text-xs text-ink-muted">
              {t('catalog.packages.noTemplates')}
            </p>
          ) : (
            <ul className="mt-1 space-y-1 text-sm">
              {grants.map((grant) => (
                <li key={grant.id}>
                  {grant.type === 'RESOURCE_TIME'
                    ? t('catalog.packages.minutesOn', {
                        minutes: (grant.minutes ?? 0) * copies,
                        target: grant.resource?.name ?? t('catalog.packages.anyResource'),
                      })
                    : t('catalog.packages.itemGrant', {
                        quantity: trimQuantity((Number(grant.quantity ?? '1') * copies).toFixed(3)),
                        name: grant.catalogItem?.name ?? '',
                      })}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs text-ink-muted">
            {t('catalog.entitlements.valueHint')}
          </p>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * §6.3 — draw units or minutes off a grant the guest already holds. The API
 * refuses over-consumption, so the input is clamped to what is left rather than
 * letting a cashier discover the limit through a 409.
 */
function ConsumeDialog({
  row,
  onClose,
  onConsumed,
}: {
  row: IssuedEntitlement;
  onClose: () => void;
  onConsumed: () => void;
}) {
  const { t } = useTranslation();
  const isTime = row.type === 'RESOURCE_TIME';
  const remainingMinutes = row.remainingMinutes ?? 0;
  const remainingUnits = Number(row.remainingQuantity);

  const [amount, setAmount] = useState(() =>
    isTime
      ? String(Math.min(30, remainingMinutes))
      : trimQuantity(String(Math.min(1, remainingUnits))),
  );
  const [busy, setBusy] = useState(false);
  const [key] = useState(() => idempotencyKey(`consume:${row.id}`));

  const value = Number(amount);
  const max = isTime ? remainingMinutes : remainingUnits;
  const valid = value > 0 && value <= max && (!isTime || Number.isInteger(value));

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const { data } = await api.post<ConsumptionResult>(
        `/packages/entitlements/${row.id}/consume`,
        {
          ...(isTime ? { minutes: value } : { quantity: value.toFixed(3) }),
          idempotencyKey: key,
        },
      );
      toast.success(
        isTime
          ? t('catalog.entitlements.redeemedMinutes', {
              minutes: data.consumedMinutes,
              remaining: data.entitlement.remainingMinutes ?? 0,
            })
          : t('catalog.entitlements.redeemedUnits', {
              units: trimQuantity(data.consumedQuantity),
              remaining: trimQuantity(data.entitlement.remainingQuantity),
            }),
      );
      onConsumed();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('catalog.entitlements.redeemFailed')));
    } finally {
      setBusy(false);
    }
  };

  const grantLabel = isTime
    ? t('catalog.packages.minutesOn', {
        minutes: row.minutes ?? 0,
        target: row.resource?.name ?? t('catalog.packages.anyResource'),
      })
    : t('catalog.packages.itemGrant', {
        quantity: trimQuantity(row.quantity),
        name: row.catalogItem?.name ?? '',
      });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('catalog.entitlements.redeemTitle', {
        name: row.check
          ? t('catalog.entitlements.checkNumber', { number: row.check.number })
          : t('catalog.entitlements.noCheck'),
      })}
      description={t('catalog.entitlements.redeemHint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !valid}>
            {t('catalog.entitlements.redeem')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border p-3 text-sm">
          <p className="font-medium">{grantLabel}</p>
          <p className="mt-1 text-xs text-ink-muted">
            {isTime
              ? t('catalog.entitlements.minutesOfMinutes', {
                  remaining: remainingMinutes,
                  granted: row.minutes ?? 0,
                })
              : t('catalog.entitlements.unitsLeft', {
                  remaining: trimQuantity(row.remainingQuantity),
                  granted: trimQuantity(row.quantity),
                })}
          </p>
        </div>

        <Field
          label={isTime ? t('catalog.entitlements.minutes') : t('common.quantity')}
          hint={
            isTime
              ? t('catalog.entitlements.maxMinutes', { minutes: remainingMinutes })
              : t('catalog.entitlements.maxUnits', { units: trimQuantity(row.remainingQuantity) })
          }
          error={!valid && amount !== '' ? t('catalog.entitlements.overDraw') : undefined}
        >
          <Input
            type="number"
            min={isTime ? 1 : 0.001}
            max={max}
            step={isTime ? 1 : 0.001}
            value={amount}
            onChange={(event) => setAmount(event.currentTarget.value)}
          />
        </Field>

        {isTime && (
          <div className="flex flex-wrap gap-2">
            {[15, 30, 60].map((preset) => (
              <Button
                key={preset}
                size="sm"
                variant="outline"
                disabled={preset > remainingMinutes}
                onClick={() => setAmount(String(preset))}
              >
                {t('catalog.entitlements.presetMinutes', { minutes: preset })}
              </Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => setAmount(String(remainingMinutes))}>
              {t('catalog.entitlements.allRemaining')}
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
